import { IDelivery } from './models/IDelivery';
import { ScatterDataPoint, ChartOptions, TimeUnit } from 'chart.js';
import moment from 'moment';
import { TimeSeriesField } from './TimeSeriesField';
import { AnnotationPluginOptions } from 'chartjs-plugin-annotation';
import regression from 'regression';

export default class ChartHelper {

  // Generate labels for each month
  getMonthLabels(startDate: moment.Moment, numMonths: number): moment.Moment[] {
    const label:
    moment.Moment[] = []
    for (let i = 0; i < numMonths; i++) {
      label.push(
        moment(startDate)
          .subtract(numMonths, 'month')
          .startOf('day')
      );
    }
    return label;
  }
  
  public getDataSetByMonths(field: TimeSeriesField, from: moment.Moment, numberOfMonths: number, deliveries: IDelivery[]): ScatterDataPoint[] {
    const toDate = moment(from).add(numberOfMonths, 'months');
    return deliveries.sort(this.compareDates)
      .filter(d => moment(d.time).isSameOrBefore(toDate) && moment(d.time).isSameOrAfter(from))
      .map(d => ({ 
        x: new Date (d.time).getTime(),
        y: this.getValue(field, d)
      }));
  }
  
  public static getRegressionOnDataset(dataset: ScatterDataPoint[]) : ScatterDataPoint[]{
    const washedDataset = dataset.filter(d => d.y != null).map(d => [d.x, d.y]);
    if (washedDataset.length == 0) return []
    const xStart = washedDataset.slice(-1)[0][0];
    const xEnd = washedDataset[0][0]

    const result = regression.linear(washedDataset, {precision: 32});
    const gradient = result.equation[0];
    const yIntercept = result.equation[1];
    
    //Linear only needs two points
    return [
      {x: xStart, y:this.linearFunc(xStart, gradient, yIntercept)}, 
      {x: xEnd, y:this.linearFunc(xEnd, gradient, yIntercept) }
    ]
  }

  // y = ax+b
  private static linearFunc(x:number, gradient:number, yIntercept:number) :number{
    return gradient*x+yIntercept;
  }

  public compareDates(a: IDelivery, b: IDelivery) {
    if (moment(a.time).isAfter(b.time)) {
      return -1;
    }
    if (moment(a.time).isBefore(b.time)) {
      return 1;
    }
    // a must be equal to b
    return 0;
  }

  getChartOptionsMonth(dataLabel: string, yAxisLabel: string): ChartOptions {
    const options = this.getChartOptions(dataLabel, yAxisLabel);
    if (options.scales === undefined || options.scales.y === undefined) return options;
    if (dataLabel === 'Fuktighet') {
      Object.assign(options.scales.y, ({suggestedMin: 7.5}));
      Object.assign(options.scales.y, ({suggestedMax: 15}));
    }
    else if (dataLabel === 'Bølgepapp'){
      Object.assign(options.scales.y, ({suggestedMin: 80}));
      Object.assign(options.scales.y, ({suggestedMax: 100}));
    }
   
    return options;
  }

   private getChartOptions(dataLabel: string, yAxisLabel: string ): ChartOptions {
    this.getAnnotation(dataLabel)
    return {
      responsive: true,
      plugins :{
        title: {
          display: true,
          text: dataLabel,
          font: {
            size: 20,
            family: "Flama-Basic",
          },
          color: "black"
        },
        legend: {
          display: true,
          labels: {
            font: {
              family: "Flama-Basic"
            }, 
          },
          onClick: function (e, legendItem, legend) {
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            if (chart.isDatasetVisible(index)) {
              chart.hide(index);
              legendItem.hidden = true;
            } else {
              chart.show(index);
              legendItem.hidden = false;
            }

            if (legendItem.text.includes("Grenseverdi")){
              if (chart.options.plugins?.annotation?.annotations?.length != undefined ){
                for (let i = 0 ; i< chart.options.plugins.annotation.annotations.length; i++){
                  Object.assign(chart.options.plugins.annotation.annotations[i], ({display: !legendItem.hidden}));
                }
                chart.update();
              }
            }
          }
        },
        
        tooltip: {
          callbacks: {
            label: function(tooltipItem) {
              const label =  dataLabel + ": " + String(Number(tooltipItem.formattedValue).toFixed(2));
              return label;
            },
            title: function(tooltipItem){
              const title = moment(tooltipItem[0].parsed.x).format('DD-MM-YYYY') ;
              return title;
            }
          }
        },
        annotation: this.getAnnotation(dataLabel),
      },
      elements: {
        point: {
          radius: 4,
          hoverRadius: 5,
          hitRadius: 4
        },
      },
      scales:{
        x: {
          type: "time",
          ticks: {
            maxTicksLimit: 15, 
            font: {
              family: "Flama-Basic",
              size:12
            },
          },
          grid: {
            display: true,
          },
          time: {
            unit: "day",
            displayFormats: {
              day: 'D MMMM YYYY',
            }
          },
          suggestedMax: new Date().getTime()
        },
        y: {
          type: 'linear',
          title: {
            text: yAxisLabel,
            display: true,
            font: {
              size: 14,
              family: "Flama-Basic",
            },
            color: "black"
          },
          ticks: {
            maxTicksLimit: 10,
            stepSize: 0.5,
            font: {
              size: 12,
              family: "Flama-Basic"
            },
          }
        },
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 20,
        }
      },
    } 
  }

  getAnnotation(dataLabel: string) : AnnotationPluginOptions {
    if (dataLabel == 'Fuktighet') {
      return {
        annotations: [{
          type: 'line',
          yMax: 10,
          yMin: 10,
          borderColor: 'red',
          borderWidth: 2,
        }],
        drawTime: 'afterDatasetsDraw'
      }
    }
    else if (dataLabel == 'Bølgepapp') {
      return {
        annotations:[{
          type: 'line',
          yMax: 85,
          yMin: 85,
          borderColor: 'red',
          borderWidth: 1,
          label: {
            backgroundColor: "red",
            content: "Bølgepapp",
            enabled: true
          }
        }
      ],
        drawTime: 'afterDatasetsDraw'
      }
    }
    
    return {} as AnnotationPluginOptions;
  }

  private getValue(f: TimeSeriesField, d: IDelivery): number {
    switch (f) {
      case TimeSeriesField.Fuktighet:
        return d.fuktighet;
      case TimeSeriesField.Plast:
        return d.plast;
      case TimeSeriesField.BP:
          return d.snittBP;
      default:
        return -1;
    }
  }

}

